import { UUID } from '@cmg/common';
import { SelectOption } from '@cmg/design-system';
import { FormikTouched } from 'formik';
import defaultTo from 'lodash/defaultTo';

import { ConvertibleFilingTalkRanges } from '../../../../../../../../common/hooks/useConvertibleFilingTalkRanges';
import { getDefaultLimitTypeForOffering } from '../../../../../../../../common/util/offering/offeringTypeHelpers';
import {
  InstitutionalIndicationOrderType,
  InterestLevelInterestUnit,
  OfferingType,
  SecurityType,
} from '../../../../../../../../graphql';
import { interestUnitLabels } from '../../../../../../../../types/domain/order-book/constants';
import { getInstrumentName } from '../../../../../../../offering-setup-next/utils';
import { SyndicateIndication_PublishedOfferings_InstrumentPartsFragment } from '../../../../context/hooks/syndicate/__generated__';
import { IndicationForm_IndicationDetails_Offering_OrderEntryProfilePartsFragment } from '../../../indication-form/components/indication-details/graphql/__generated__';
import { IndicationForm_Indication_InterestLevelPartsFragment } from '../../../indication-form/graphql/__generated__';
import { ConvertibleOrderType, getConvertibleOrderType } from '../utils/getConvertibleOrderType';
import {
  emptyInterestLevelValue,
  IndicationFormValues,
  InterestLevelValue,
} from '../utils/IndicationSectionValidation';

export const getTrancheOptions = (
  tranches: IndicationForm_IndicationDetails_Offering_OrderEntryProfilePartsFragment['tranches']
): SelectOption<UUID>[] => {
  return tranches.map(({ name, id }) => ({ label: name, value: id }));
};

type GetInstrumentOptionsParams = {
  issuerName: string | null;
  instruments: Readonly<SyndicateIndication_PublishedOfferings_InstrumentPartsFragment[]> | null;
  selectedTranche:
    | IndicationForm_IndicationDetails_Offering_OrderEntryProfilePartsFragment['tranches'][number]
    | undefined;
  securityTypeDisplayName: string;
};

export const getInstrumentOptions = ({
  issuerName,
  instruments,
  selectedTranche,
  securityTypeDisplayName,
}: GetInstrumentOptionsParams): SelectOption<UUID>[] => {
  if (!selectedTranche) {
    return [];
  }

  const selectedInstruments = instruments?.filter(({ id }) =>
    selectedTranche.deliveryInstrumentIds?.includes(id)
  )!;

  return selectedInstruments.map(instrument => ({
    label: defaultTo(
      instrument?.name,
      getInstrumentName({
        issuerName,
        securityTypeDisplayName,
        stockSymbol: instrument?.stockSymbol ?? null,
        isDepositaryReceipt: !!instrument?.isDepositaryReceipt,
      })
    ),
    value: instrument.id,
  }));
};

export const getCurrencyOptions = (
  selectedTranche:
    | IndicationForm_IndicationDetails_Offering_OrderEntryProfilePartsFragment['tranches'][number]
    | undefined
): SelectOption<string>[] => {
  if (!selectedTranche) {
    return [];
  }

  return selectedTranche.demandCurrencies.map(currency => ({ label: currency, value: currency }));
};

export const getDefaultOrderType = (
  interestLevelsContainNonEmptyOrderType: boolean,
  isLimitIndicationsOnlyEnabled: boolean,
  offeringType: OfferingType
) => {
  if (offeringType === OfferingType.Convertible) {
    return null;
  }
  if (isLimitIndicationsOnlyEnabled) {
    return InstitutionalIndicationOrderType.Limit;
  }

  return interestLevelsContainNonEmptyOrderType ? InstitutionalIndicationOrderType.Limit : null;
};

export const createInterestLevelNewValue = (
  interestLevels: InterestLevelValue[],
  offeringType: OfferingType,
  isLimitIndicationsOnlyEnabled: boolean,
  isOnlySharesUnitOnIndicationsEnabled: boolean,
  selectedInterestUnit?: InterestLevelInterestUnit | null
): InterestLevelValue => {
  const defaultInterestUnit = isOnlySharesUnitOnIndicationsEnabled
    ? InterestLevelInterestUnit.Shares
    : null;

  const inheritedInterestUnit = defaultTo(
    selectedInterestUnit,
    interestLevels[0]?.interestUnit ?? defaultInterestUnit
  );

  const interestLevelsContainNonEmptyOrderType = interestLevels.some(
    ({ orderType }) => !!orderType
  );
  // default to Limit order type when there is order with existing orderType
  const orderType = getDefaultOrderType(
    interestLevelsContainNonEmptyOrderType,
    isLimitIndicationsOnlyEnabled,
    offeringType
  );

  const defaultLimitType =
    orderType === InstitutionalIndicationOrderType.Limit
      ? getDefaultLimitTypeForOffering(offeringType)
      : null;
  const selectedLimitType =
    interestLevels.find(({ limitType }) => !!limitType)?.limitType ?? defaultLimitType;

  return {
    ...emptyInterestLevelValue,
    interestUnit: inheritedInterestUnit,
    orderType,
    // default to selected limit type when we default to Limit order type
    limitType: interestLevelsContainNonEmptyOrderType ? selectedLimitType : defaultLimitType,
  };
};

type GetInterestLevelOrderTypeParams = {
  interestLevel: Pick<
    IndicationForm_Indication_InterestLevelPartsFragment,
    'coupon' | 'premium' | 'percentageOfPar' | 'limitPrice'
  >;
  offeringType: OfferingType | null;
  convertibleFilingTalkRanges: ConvertibleFilingTalkRanges;
  isIndicationPriceAsPercentageParEnabled: boolean;
};

// eslint-disable-next-line sonarjs/function-return-type
export function getInterestLevelOrderType({
  interestLevel,
  offeringType,
  convertibleFilingTalkRanges,
  isIndicationPriceAsPercentageParEnabled,
}: GetInterestLevelOrderTypeParams): InstitutionalIndicationOrderType | ConvertibleOrderType {
  if (offeringType !== OfferingType.Convertible) {
    return interestLevel?.limitPrice
      ? InstitutionalIndicationOrderType.Limit
      : InstitutionalIndicationOrderType.Market;
  }

  return getConvertibleOrderType({
    coupon: defaultTo(interestLevel.coupon, null),
    premium: defaultTo(interestLevel.premium, null),
    percentageOfPar: defaultTo(interestLevel.percentageOfPar, null),
    isIndicationPriceAsPercentageParEnabled,
    couponTalkLow: defaultTo(convertibleFilingTalkRanges?.couponTalkLow, null),
    couponTalkHigh: defaultTo(convertibleFilingTalkRanges?.couponTalkHigh, null),
    premiumTalkLow: defaultTo(convertibleFilingTalkRanges?.premiumTalkLow, null),
    premiumTalkHigh: defaultTo(convertibleFilingTalkRanges?.premiumTalkHigh, null),
    priceAsOfPar: defaultTo(convertibleFilingTalkRanges?.priceAsOfPar, 1),
  });
}

export function getConvertibleDemandUnitOptions(securityType: SecurityType | null) {
  return [
    securityType === SecurityType.ConvertibleBond
      ? {
          label: interestUnitLabels[InterestLevelInterestUnit.Bonds],
          value: InterestLevelInterestUnit.Bonds,
        }
      : {
          label: interestUnitLabels[InterestLevelInterestUnit.Shares],
          value: InterestLevelInterestUnit.Shares,
        },

    {
      label: interestUnitLabels[InterestLevelInterestUnit.Currency],
      value: InterestLevelInterestUnit.Currency,
    },
    {
      label: interestUnitLabels[InterestLevelInterestUnit.Percent],
      value: InterestLevelInterestUnit.Percent,
    },
  ];
}

export const convertibleOrderTypeOptions = Object.values(ConvertibleOrderType).map(value => ({
  label: value,
  value,
}));

type GetInitialTouchedFieldsParams = {
  interestLevels: InterestLevelValue[];
  isIndicationPriceAsPercentageParEnabled: boolean;
  offeringType: OfferingType;
};

export function getInitialTouchedFields({
  interestLevels,
  offeringType,
  isIndicationPriceAsPercentageParEnabled,
}: GetInitialTouchedFieldsParams): FormikTouched<IndicationFormValues> {
  if (offeringType === OfferingType.Convertible && !isIndicationPriceAsPercentageParEnabled) {
    return interestLevels.reduce<FormikTouched<IndicationFormValues>>(
      (acc, interestLevel, index) => {
        if (interestLevel.percentageOfPar !== null) {
          acc.interestLevels ??= [];
          acc.interestLevels[index] = { percentageOfPar: true };
        }
        return acc;
      },
      { interestLevels: [] }
    );
  }

  return {};
}
